import React from 'react'
import styled, { css, StyledProps } from 'styled-components'
import { Link } from 'react-router-dom'

export const MenuContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  height: 100%;

  ${props => props.theme.media.desktop`
    padding: 24px;
    width: 302px;
  `}

  ${props => props.theme.media.tablet`
    width: 280px;
    padding: 24px 12px;
  `}

  ${props => props.theme.media.smallTablet`
    flex-direction: row;
    width: 100%;
  `}

  ${props => props.theme.media.mobile`
    flex-direction: row;
    width: 100%;
    padding: 12px 16px 16px;
  `}

  &[data-compact-menu="true"] {
    width: auto;
  }
`

const InnerWrapper = styled.div<StyledProps<{ active?: boolean }>>`
  display: inline-flex;
  white-space: nowrap;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: flex-start;

  ${props => props.theme.media.mobile`
    display: ${({ active }: { active: boolean }) => (active ? 'inline-flex' : 'none')};
  `}

  [data-compact-menu="true"] & {
    display: none;
  }
`

const IconWrapper = styled.div<StyledProps<{ active?: boolean }>>`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 10px;

  > svg {
    transition: 0.3s fill;
    width: 24px;
    fill: ${({ theme, active }: StyledProps<{ active?: boolean }>) =>
      active ? theme.color.primary600 : theme.color.secondary200};
  }

  ${props => props.theme.media.mobile`
      margin-right: ${({ active }: StyledProps<{ active?: boolean }>) => (active ? '10px' : 0)};
  `}

  [data-compact-menu="true"] & {
    margin-right: 0;
  }
`

const ContentWrapper = styled.div`
  white-space: break-spaces;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`

const TextWrapper = styled.div`
  margin-right: 15px;
`

type ItemStyledProps = StyledProps<{ active?: boolean }>

const ItemStyle = css`
  border-radius: 8px;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  padding: 0 12px;
  transition: 0.3s background-color, 0.3s color;
  border: none;
  color: ${({ active, theme }: ItemStyledProps) => (active ? theme.color.primary600 : theme.color.onPrimaryDark)};
  background-color: ${({ active, theme }: ItemStyledProps) =>
    active ? theme.color.primary25 : theme.color.onPrimaryLight};
  cursor: ${({ active }: ItemStyledProps) => (active ? 'default' : 'pointer')};

  &:hover {
    color: ${({ theme }: ItemStyledProps) => theme.color.primary600};

    > ${IconWrapper} svg {
      fill: ${({ theme }: ItemStyledProps) => theme.color.primary500};
    }
  }

  &:focus {
    outline: none;
  }

  &:not(:first-child) {
    margin-top: 8px;

    ${props => props.theme.media.mobile`
      margin-top: 0;
      padding: 0 ${({ active }: ItemStyledProps) => (active ? '12px' : '16px')};
    `}

    ${props => props.theme.media.tablet`
      margin-top: 0;
      padding: 0 ${({ active }: ItemStyledProps) => (active ? '12px' : '16px')};
    `}
  }

  ${props => props.theme.media.mobile`
    margin-top: 0;
    font-size: 12px;
    width: auto;
    display: grid;
    grid-template-columns: max-content max-content;
    grid-gap: 0;
  `}

  ${props => props.theme.media.smallTablet`
    width: auto;
  `}

  [data-compact-menu="true"] & {
    width: 48px;
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ItemLinkStyled = styled(({ active, ...props }) => <Link {...props} />)<ItemStyledProps>`
  position: relative;
  ${ItemStyle}
`

const ItemNotLinkStyled = styled.div<ItemStyledProps>`
  position: relative;
  ${ItemStyle}
`

const ItemStyled = styled.button<ItemStyledProps>`
  ${ItemStyle}
`

type SVGIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>

type ItemProps = {
  active?: boolean
  icon: SVGIcon
  onClick?: React.MouseEventHandler
  className?: string
}

export type ItemLinkProps = ItemProps & {
  path?: string
  count?: number
  onClick?: React.MouseEventHandler
  overlay?: React.ReactNode
  overlayOpen?: boolean
  children?: React.ReactChildren | React.ReactNode | React.ReactNodeArray
}

const Count = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.color.onPrimaryLight};
  font-size: 10px;
  line-height: 16px;
  min-width: 14px;
  height: 14px;
  border-radius: 8px;
  background-color: ${props => props.theme.color.accentNotify};
  top: 50%;
  right: 8px;
  padding: 0px 6px;
  transform: translateY(-50%);
  font-weight: 600;
`

export const BarMenuFullWidthItem = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.onSurfaceLowEmphasys};
  transition: 0.3s background-color, 0.3s color;
  &:hover {
    color: ${({ theme }: ItemStyledProps) => theme.color.primary600};
  }

  cursor: pointer;
`

export const MenuItemLink = React.forwardRef<any, ItemLinkProps>(
  (
    { children, active, icon: IconComponent, path, count, className, onClick, overlay = null, overlayOpen, ...rest },
    ref,
  ) => {
    const inner = (
      <>
        <ContentWrapper onClick={onClick}>
          <TextWrapper>{children}</TextWrapper>
          {count ? <Count>{count}</Count> : null}
        </ContentWrapper>
        {overlayOpen ? overlay : null}
      </>
    )

    if (path) {
      return (
        <ItemLinkStyled {...rest} active={active} to={path || ''} className={className} ref={ref}>
          <IconWrapper active={active}>
            <IconComponent />
          </IconWrapper>
          <InnerWrapper active={active}>{inner}</InnerWrapper>
        </ItemLinkStyled>
      )
    }

    return (
      <ItemNotLinkStyled {...rest} active={active} className={className} ref={ref}>
        <IconWrapper active={active}>
          <IconComponent />
        </IconWrapper>
        <InnerWrapper active={active}>{inner}</InnerWrapper>
      </ItemNotLinkStyled>
    )
  },
)

type MenuProps = {
  className?: string
  compact?: boolean
  children?: React.ReactNode
}

export const MenuContainer: React.FC<MenuProps> = ({ children, className, compact }) => {
  return (
    <MenuContainerStyled className={className} data-compact-menu={compact}>
      {children}
    </MenuContainerStyled>
  )
}

MenuItemLink.displayName = 'MenuItemLink'

export const MenuItem: React.FC<ItemProps> = ({ children, active, icon: IconComponent, onClick, className }) => {
  return (
    <ItemStyled active={active} onClick={onClick} className={className}>
      {IconComponent && (
        <IconWrapper active={active}>
          <IconComponent />
        </IconWrapper>
      )}
      <InnerWrapper active={active}>{children}</InnerWrapper>
    </ItemStyled>
  )
}
